/**
 * EvVideo component - Handles Rutube video loading on demand
 */
class EvVideo {
    constructor() {
        this.selectors = {
            container: '.evvideo',
            poster: '.evvideo__poster',
            playButton: '.evvideo__play-btn',
            videoContainer: '.evvideo__video-container',
        }
        this.instances = []
    }

    /**
     * Initialize all video instances on the page
     */
    init() {
        const containers = document.querySelectorAll(this.selectors.container)

        containers.forEach(container => {
            this.initInstance(container)
        })
    }

    /**
     * Initialize a single video instance
     * @param {HTMLElement} container - The video container element
     */
    initInstance(container) {
        const poster = container.querySelector(this.selectors.poster)
        const playButton = container.querySelector(this.selectors.playButton)
        const videoContainer = container.querySelector(this.selectors.videoContainer)

        if (!poster || !playButton || !videoContainer) {
            // eslint-disable-next-line no-console
            console.warn('EvVideo: Missing required elements in container', container)

            return
        }

        const videoType = poster.dataset.videoType
        const videoId = poster.dataset.videoId
        const rutubeUrl = poster.dataset.rutubeUrl

        if (!videoType && (!rutubeUrl || rutubeUrl === '#')) {
            // eslint-disable-next-line no-console
            console.warn('EvVideo: No valid video URL or ID found', container)

            return
        }

        const instance = {
            container,
            poster,
            playButton,
            videoContainer,
            videoType,
            videoId,
            rutubeUrl,
            isLoaded: false,
        }

        // Add click handlers
        playButton.addEventListener('click', () => this.handlePlayClick(instance))
        poster.addEventListener('click', () => this.handlePlayClick(instance))

        // Add keyboard support
        playButton.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                this.handlePlayClick(instance)
            }
        })

        this.instances.push(instance)
    }

    /**
     * Handle play button click
     * @param {Object} instance - The video instance
     */
    handlePlayClick(instance) {
        if (instance.isLoaded) {
            return
        }

        // Всё действие синхронно — сохраняем user gesture
        const videoId = instance.videoId
        const iframe = document.createElement('iframe')
        iframe.className = 'dzen-iframe'
        iframe.width = '100%'
        iframe.height = '480'
        iframe.src = `https://dzen.ru/embed/${videoId}?from_block=partner&from=zen&mute=0&autoplay=1&tv=0`
        iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media'
        iframe.frameBorder = '0'
        iframe.scrolling = 'no'
        iframe.setAttribute('allowfullscreen', '')

        // очистка и вставка — всё в одном клике
        instance.videoContainer.innerHTML = ''
        instance.videoContainer.appendChild(iframe)
        instance.poster.style.display = 'none'

        instance.isLoaded = true
    }

    /**
     * Load the video (Dzen or Rutube)
     * @param {Object} instance - The video instance
     */
    loadVideo(instance) {
        let embedUrl

        if (instance.videoType === 'dzen' && instance.videoId) {
            embedUrl = this.getDzenEmbedUrl(instance.videoId)
        } else if (instance.rutubeUrl) {
            embedUrl = this.getRutubeEmbedUrl(instance.rutubeUrl)
        }

        if (!embedUrl) {
            // eslint-disable-next-line no-console
            console.error('EvVideo: Could not generate embed URL')

            if (instance.rutubeUrl) {
                window.open(instance.rutubeUrl, '_blank')
            }

            return
        }

        // Create iframe with all attributes set before adding to DOM
        const iframe = document.createElement('iframe')
        iframe.className = instance.videoType === 'dzen' ? 'dzen-iframe' : ''
        iframe.width = '100%'
        iframe.height = '480'
        iframe.frameBorder = '0'
        iframe.scrolling = 'no'
        iframe.allowFullscreen = true

        // Set src FIRST before other attributes
        iframe.src = embedUrl

        // Critical: Set allow attribute for autoplay
        iframe.setAttribute(
            'allow',
            'autoplay; fullscreen; accelerometer; gyroscope; picture-in-picture; encrypted-media',
        )

        if (instance.videoType === 'dzen') {
            iframe.title = 'Dzen Video Player'
        } else {
            iframe.title = 'Rutube Video Player'
        }

        // Add loading state
        instance.container.classList.add('evvideo--loading')

        // Handle iframe load
        iframe.addEventListener('load', () => {
            instance.container.classList.remove('evvideo--loading')
            instance.container.classList.add('evvideo--loaded')
        })

        // Handle iframe error
        iframe.addEventListener('error', () => {
            instance.container.classList.remove('evvideo--loading')
            // eslint-disable-next-line no-console
            console.error('EvVideo: Failed to load video iframe')
            // Fallback: open in new tab
            if (instance.rutubeUrl) {
                window.open(instance.rutubeUrl, '_blank')
            }
        })

        // Hide poster immediately (before adding iframe)
        instance.poster.style.display = 'none'

        // Clear video container and add iframe
        instance.videoContainer.innerHTML = ''
        instance.videoContainer.style.display = 'block'
        instance.videoContainer.appendChild(iframe)

        instance.isLoaded = true
    }

    /**
     * Generate Dzen embed URL from video ID
     * @param {string} videoId - The Dzen video ID
     * @returns {string} - The embed URL
     */
    getDzenEmbedUrl(videoId) {
        // Updated URL parameters for better autoplay support
        return `https://dzen.ru/embed/${videoId}?from_block=partner&from=zen&mute=0&autoplay=1&tv=0&startPosition=0`
    }

    /**
     * Convert Rutube URL to embed URL
     * @param {string} url - The Rutube video URL
     * @returns {string|null} - The embed URL or null if invalid
     */
    getRutubeEmbedUrl(url) {
        try {
            // Handle different Rutube URL formats
            let videoId = null

            // Standard Rutube video URL: https://rutube.ru/video/[VIDEO_ID]/
            const standardMatch = url.match(/rutube\.ru\/video\/([a-zA-Z0-9]+)/)
            if (standardMatch) {
                videoId = standardMatch[1]
            }

            // Rutube play URL: https://rutube.ru/play/embed/[VIDEO_ID]
            const playMatch = url.match(/rutube\.ru\/play\/embed\/([a-zA-Z0-9]+)/)
            if (playMatch) {
                videoId = playMatch[1]
            }

            // Direct embed URL (already formatted)
            if (url.includes('rutube.ru/play/embed/')) {
                return url
            }

            if (!videoId) {
                // eslint-disable-next-line no-console
                console.warn('EvVideo: Could not extract video ID from URL:', url)

                return null
            }

            // Return embed URL with autoplay parameter
            return `https://rutube.ru/play/embed/${videoId}/?skinColor=181818`

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('EvVideo: Error parsing Rutube URL:', error)

            return null
        }
    }

    /**
     * Destroy all instances (cleanup)
     */
    destroy() {
        this.instances.forEach(instance => {
            instance.playButton.removeEventListener('click', () => this.handlePlayClick(instance))
            instance.poster.removeEventListener('click', () => this.handlePlayClick(instance))
        })
        this.instances = []
    }
}

// Create singleton instance
const evVideo = new EvVideo()

export default evVideo
